import React, { useRef, useState } from 'react';
import { CheckCircle2, ImageOff, Loader2, Upload } from 'lucide-react';
import { formatBytes, resizeImage } from '../../utils/imageResize';

/**
 * Generic image chooser: paste a URL or upload a file.
 *
 * Uploads are stored as data URIs inside the home page config, which now lives
 * in one row on the server. That keeps deployment simple — no asset pipeline,
 * no upload endpoint, no second thing to back up — but base64 adds a third on
 * top of the file, and every signed-in user downloads this image on every
 * visit to the portal.
 *
 * So the cap is not a limit on what someone may PICK; it is a limit on what is
 * worth reading. Anything accepted is re-encoded on the way in (see
 * utils/imageResize) until it fits comfortably, because a photo straight off a
 * phone is measured in megabytes — which the API would reject outright, and
 * which would make the home page slow for everyone if it did not.
 */
export default function ImagePicker({
  label,
  value,
  onChange,
  hint,
  maxBytes = 2 * 1024 * 1024,
  maxDimension = 512,
  targetBytes = 200 * 1024,
  previewAspect = 'square',
}: {
  label: string;
  value: string | undefined;
  onChange: (url: string | undefined) => void;
  hint?: string;
  /** Largest file that will be accepted for processing. */
  maxBytes?: number;
  /** Longest edge kept after scaling. */
  maxDimension?: number;
  /** What the stored data URI is squeezed towards. */
  targetBytes?: number;
  /** `wide` suits background images; `square` suits logos. */
  previewAspect?: 'square' | 'wide';
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    setError(null);
    setNote(null);
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Choose an image file.');
      return;
    }
    if (file.size > maxBytes) {
      setError(
        `That file is ${formatBytes(file.size)} — the limit is ${formatBytes(maxBytes)}. Use a smaller file, or paste a URL instead.`,
      );
      return;
    }

    setBusy(true);
    try {
      const resized = await resizeImage(file, { maxDimension, targetBytes });
      onChange(resized.dataUrl);
      // Says what was done rather than doing it invisibly: someone who
      // uploaded a 4 MB photo should know what is actually being stored.
      setNote(
        resized.changed
          ? `${formatBytes(resized.originalBytes)} → ${formatBytes(resized.storedBytes)} · ${resized.width}×${resized.height}`
          : `${formatBytes(resized.storedBytes)} stored`,
      );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not read that file.');
    } finally {
      setBusy(false);
    }
  };

  // An uploaded file is stored as a data URI — dumping that (often tens of
  // thousands of characters) into a plain text box reads as broken, and
  // there is nothing meaningful to type or paste over it anyway. Shown as a
  // plain confirmation instead; "Use a URL instead" clears it back to an
  // editable box for anyone who wants to link an image rather than upload
  // one.
  const isUploadedData = (value ?? '').startsWith('data:');

  return (
    <div className="space-y-2">
      <span className="text-xs font-semibold text-slate-600">{label}</span>

      <div className="flex items-start gap-3">
        <div
          className={`flex flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50 ${
            previewAspect === 'wide' ? 'h-16 w-28' : 'h-16 w-16'
          }`}
        >
          {value ? (
            <img
              src={value}
              alt={`${label} preview`}
              className="h-full w-full object-cover"
              onError={() => setError('That image could not be loaded.')}
            />
          ) : (
            <ImageOff className="h-5 w-5 text-slate-300" />
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
        {isUploadedData ? (
          <div className="flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-[11px] text-teal-800">
            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="min-w-0 flex-1 truncate">Image uploaded</span>
            <button
              type="button"
              onClick={() => onChange(undefined)}
              className="flex-shrink-0 font-semibold text-teal-700 underline-offset-2 hover:underline"
            >
              Use a URL instead
            </button>
          </div>
        ) : (
          <input
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value.trim() || undefined)}
            placeholder="https://example.com/image.png"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
          />
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {busy ? 'Processing…' : 'Upload image'}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => {
                setError(null);
                onChange(undefined);
              }}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-red-600 transition hover:bg-red-50"
            >
              Remove
            </button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => {
            void handleFile(event.target.files?.[0]);
            // Allow re-picking the same file.
            event.target.value = '';
          }}
        />

        {error ? (
          <p role="alert" className="text-[11px] text-red-600">
            {error}
          </p>
        ) : note ? (
          <p className="text-[11px] font-medium tabular-nums text-teal-700">{note}</p>
        ) : (
          hint && <p className="text-[11px] text-slate-400">{hint}</p>
        )}
        </div>
      </div>
    </div>
  );
}
